import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { getDdqIssuerUidFromProduct, getDdqProductUidFromProduct } from '@core/models/product.model';
import { OrganizationQuery } from '@core/organization/organization.query';
import { ClientDdqScopeCacheService } from './client-ddq-scope-cache.service';
import {
  getClientDdqRouteProductUidFromUrl,
  isClientDdqProductUrlPath,
  isClientInvestorDashboardShellPath,
} from './client-ddq-emittent-api-scope.util';

export interface OrganizationContext {
  viewAs?: 'investor' | 'emittent' | 'client';
  serviceType?: 'emittent' | 'investor';
  context?: string; // e.g., 'fund-ddq-runs'
  issuerUid?: string; // Issuer UID to use in URL construction when in fund-ddq-runs context
  /** Issuer-side product UID for APIs when client route param is a different identifier */
  ddqProductUid?: string;
}

/**
 * Service to manage organization context overrides for special navigation scenarios.
 * Used when displaying product details from fund DDQ runs context where:
 * - Navigation should show as "investor" (for UI display)
 * - Service calls should default to "emittent" (for API compatibility)
 */
@Injectable({ providedIn: 'root' })
export class OrganizationContextService {
  private contextOverride$ = new BehaviorSubject<OrganizationContext | null>(null);
  private readonly productDetailUrlPattern = /\/product\/[^/]+(\/|$)/;

  constructor(
    private router: Router,
    private organizationQuery: OrganizationQuery,
    private clientDdqScopeCacheService: ClientDdqScopeCacheService,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.computeContextFromRouter()),
      )
      .subscribe((context) => {
        this.applyContextFromNavigation(context);
      });

    // Initialize with current route context
    this.contextOverride$.next(this.computeContextFromRouter());
  }

  /**
   * Query params (legacy / shareable links) take precedence; otherwise derive fund-ddq-runs
   * from client `/client/:productId/ddq/...` resolved `summary` so URLs stay clean.
   */
  private computeContextFromRouter(): OrganizationContext | null {
    const pathOnly = (this.router.url ?? '').split('?')[0];
    if (isClientInvestorDashboardShellPath(pathOnly)) {
      return null;
    }

    const fromQuery = this.extractContextFromUrl();
    if (fromQuery?.context === 'fund-ddq-runs') {
      return fromQuery;
    }
    const fromSummary = this.extractFundDdqContextFromClientDdqRouteData();
    if (fromSummary) {
      return fromSummary;
    }
    const fromCache = this.extractFundDdqContextFromCache();
    if (fromCache) {
      return fromCache;
    }
    if (isClientDdqProductUrlPath(pathOnly)) {
      const existing = this.contextOverride$.getValue();
      if (existing?.context === 'fund-ddq-runs' && existing?.issuerUid) {
        return existing;
      }
    }
    return null;
  }

  private walkRouteSnapshots(snapshot: ActivatedRouteSnapshot | null, visitor: (s: ActivatedRouteSnapshot) => void): void {
    if (!snapshot) {
      return;
    }
    visitor(snapshot);
    for (const child of snapshot.children ?? []) {
      this.walkRouteSnapshots(child, visitor);
    }
  }

  private extractFundDdqContextFromClientDdqRouteData(): OrganizationContext | null {
    const urlPath = (this.router.url ?? '').split('?')[0];
    if (!isClientDdqProductUrlPath(urlPath)) {
      return null;
    }
    const root = this.router.routerState?.snapshot?.root;
    if (!root) {
      return null;
    }
    let found: OrganizationContext | null = null;
    this.walkRouteSnapshots(root, (s) => {
      if (found) {
        return;
      }
      const summary = s.data?.['summary'] as { productOpening?: { ddqSummary?: unknown } } | undefined;
      if (!summary?.productOpening) {
        return;
      }
      const opening = summary.productOpening as any;
      const issuerUid = getDdqIssuerUidFromProduct(opening);
      if (issuerUid) {
        const ddqProductUid = getDdqProductUidFromProduct(opening) ?? undefined;
        found = {
          context: 'fund-ddq-runs',
          issuerUid,
          viewAs: 'investor',
          serviceType: 'emittent',
          ...(ddqProductUid ? { ddqProductUid } : {}),
        };
      }
    });
    return found;
  }

  private extractFundDdqContextFromCache(): OrganizationContext | null {
    const pathOnly = (this.router.url ?? '').split('?')[0];
    if (!isClientDdqProductUrlPath(pathOnly)) {
      return null;
    }
    const routeProductUid = getClientDdqRouteProductUidFromUrl(this.router.url);
    const cached = this.clientDdqScopeCacheService.getSnapshotForRouteProduct(routeProductUid);
    if (!cached?.issuerUid) {
      return null;
    }
    return {
      context: 'fund-ddq-runs',
      issuerUid: cached.issuerUid,
      viewAs: 'investor',
      serviceType: 'emittent',
      ...(cached.ddqProductUid ? { ddqProductUid: cached.ddqProductUid } : {}),
    };
  }

  /**
   * Extract context from current URL query parameters
   * Can also extract from a provided URL string (useful during navigation)
   * Preserves issuerUid from existing context if not in URL but context is still fund-ddq-runs
   */
  private extractContextFromUrl(url?: string): OrganizationContext | null {
    const urlToParse = url || this.router.url;
    const pathOnly = urlToParse.split('#')[0].split('?')[0];
    const urlTree = this.router.parseUrl(urlToParse);
    const queryParams = urlTree.queryParams;

    /** Keep query-driven DDQ scope only on routes that must preserve emittent API context. */
    if (queryParams['context'] === 'fund-ddq-runs' && !this.shouldHonorFundDdqQueryContext(pathOnly)) {
      return null;
    }

    if (queryParams['context'] === 'fund-ddq-runs') {
      // Preserve issuerUid from existing context if not in URL
      const existingContext = this.contextOverride$.getValue();
      const issuerUid = queryParams['issuerUid'] || existingContext?.issuerUid || undefined;
      const routeProductUid = getClientDdqRouteProductUidFromUrl(pathOnly);
      const ddqProductUid = routeProductUid || existingContext?.ddqProductUid || undefined;

      return {
        context: 'fund-ddq-runs',
        viewAs: (queryParams['viewAs'] as 'investor' | 'emittent' | 'client') || 'investor',
        serviceType: 'emittent', // Default service calls to emittent for compatibility
        issuerUid: issuerUid, // Extract from query params or preserve from existing context
        ...(ddqProductUid ? { ddqProductUid } : {}),
      };
    }

    return null;
  }

  /** Fund DDQ query context is valid for client DDQ routes and product detail routes opened from Fund DDQ list. */
  private shouldHonorFundDdqQueryContext(pathOnly: string): boolean {
    return isClientDdqProductUrlPath(pathOnly) || this.productDetailUrlPattern.test(pathOnly);
  }

  /**
   * Manually set context (useful before navigation when query params aren't in URL yet)
   */
  setContext(context: OrganizationContext): void {
    this.contextOverride$.next(context);
  }

  /**
   * Client DDQ routes without `?context=fund-ddq-runs` rely on resolver summary, scope cache,
   * or a prior `setContext` from fund-ddq navigation. Call before product-scoped API requests.
   */
  public ensureFundDdqContextForClientDdqRoute(activatedRoute?: ActivatedRoute): void {
    const pathOnly = (this.router.url ?? '').split('?')[0];
    if (!isClientDdqProductUrlPath(pathOnly)) {
      return;
    }

    if (activatedRoute) {
      this.primeFundDdqContextFromActivatedRoute(activatedRoute);
    }
    if (this.isInFundDDQRunsContextSync()) {
      return;
    }

    const fromCache = this.extractFundDdqContextFromCache();
    if (fromCache?.issuerUid) {
      this.setContext(fromCache);
    }
  }

  private applyContextFromNavigation(context: OrganizationContext | null): void {
    let next = context;
    const pathOnly = (this.router.url ?? '').split('?')[0];

    if (next?.context === 'fund-ddq-runs' && !next.issuerUid) {
      const existingContext = this.contextOverride$.getValue();
      if (existingContext?.issuerUid) {
        next = { ...next, issuerUid: existingContext.issuerUid };
      }
    }

    // Clean URLs: do not wipe fund-ddq scope when summary is not on the snapshot yet.
    if (!next?.issuerUid && isClientDdqProductUrlPath(pathOnly)) {
      const cached = this.extractFundDdqContextFromCache();
      const existing = this.contextOverride$.getValue();
      next =
        cached ??
        (existing?.context === 'fund-ddq-runs' && existing?.issuerUid ? existing : null);
    }

    this.contextOverride$.next(next);
  }

  /**
   * Re-apply fund-ddq-runs context from the route's resolved `summary` (parent DDQ tab).
   * Needed when `NavigationEnd` cleared context from the router snapshot, or before the first
   * data-room HTTP call while the URL has no `?context=fund-ddq-runs` query params.
   */
  primeFundDdqContextFromActivatedRoute(activatedRoute: ActivatedRoute): void {
    const pathOnly = (this.router.url ?? '').split('?')[0];
    if (!isClientDdqProductUrlPath(pathOnly)) {
      return;
    }
    let snap: ActivatedRouteSnapshot | null = activatedRoute.snapshot;
    while (snap) {
      const summary = snap.data?.['summary'] as { productOpening?: unknown } | undefined;
      if (summary?.productOpening) {
        const opening = summary.productOpening as any;
        const issuerUid = getDdqIssuerUidFromProduct(opening);
        if (!issuerUid) {
          break;
        }
        const ddqProductUid = getDdqProductUidFromProduct(opening) ?? undefined;
        this.setContext({
          context: 'fund-ddq-runs',
          issuerUid,
          viewAs: 'investor',
          serviceType: 'emittent',
          ...(ddqProductUid ? { ddqProductUid } : {}),
        });
        return;
      }
      snap = snap.parent;
    }

    const fromCache = this.extractFundDdqContextFromCache();
    if (fromCache?.issuerUid) {
      this.setContext(fromCache);
    }
  }


  /**
   * Get the effective organization type for service calls.
   * Returns 'emittent' when in fund-ddq-runs context, otherwise returns actual organization type.
   */
  getEffectiveOrganizationType(): Observable<string | null> {
    return combineLatest([
      this.organizationQuery.selectChosenOrganizationType().pipe(startWith(null)),
      this.contextOverride$,
    ]).pipe(
      map(([actualType, override]) => {
        if (override?.serviceType) {
          return override.serviceType;
        }
        return actualType;
      }),
    );
  }

  /**
   * Get the effective organization type synchronously (for use in synchronous methods).
   * Returns 'emittent' when in fund-ddq-runs context, otherwise returns actual organization type.
   */
  getEffectiveOrganizationTypeSync(): string | null {
    const contextValue = this.getContextValue();
    if (contextValue?.serviceType) {
      return contextValue.serviceType;
    }
    const organization = this.organizationQuery.getActive();
    return organization?.type || null;
  }

  /**
   * Get the effective organization ID synchronously.
   * Returns issuerUid when in fund-ddq-runs context, otherwise returns organization.id.
   */
  getEffectiveOrganizationIdSync(): string | null {
    const contextValue = this.getContextValue();
    if (contextValue?.context === 'fund-ddq-runs' && contextValue?.issuerUid) {
      return contextValue.issuerUid;
    }
    const organization = this.organizationQuery.getActive();
    return organization?.id || null;
  }

  /**
   * Check if we're in fund-ddq-runs context synchronously.
   */
  isInFundDDQRunsContextSync(): boolean {
    const contextValue = this.getContextValue();
    return contextValue?.context === 'fund-ddq-runs';
  }

  /**
   * Get the view-as type for navigation display.
   * Returns override value if set, otherwise returns null (use actual type).
   */
  getViewAsType(): Observable<string | null> {
    return this.contextOverride$.pipe(map((override) => override?.viewAs || null));
  }

  /**
   * Check if we're in a special context (e.g., fund-ddq-runs)
   */
  isInContext(contextName: string): Observable<boolean> {
    return this.contextOverride$.pipe(map((override) => override?.context === contextName));
  }

  /**
   * Get the full context override
   */
  getContext(): Observable<OrganizationContext | null> {
    return this.contextOverride$.asObservable();
  }

  /**
   * Get the current context value synchronously (for use in synchronous methods)
   */
  getContextValue(): OrganizationContext | null {
    return this.contextOverride$.getValue();
  }

  /**
   * Clear context override (useful for manual cleanup)
   */
  clearContext(): void {
    this.contextOverride$.next(null);
  }
}
